import { supabase } from "@/lib/supabase.js";

/**
 * Uploads a workspace image by converting it to base64 and returning the encoded string
 * This bypasses Supabase Storage completely to avoid UUID parsing issues
 */
export const uploadWorkspaceImage = async (workspace, image) => {
  if (!workspace || !image) {
    throw new Error("Workspace and image are required");
  }

  const bucket = "avatars";
  const imageSizeLimit = 6000000; // 6MB (same as assistant images)

  if (image.size > imageSizeLimit) {
    throw new Error(`Image must be less than ${imageSizeLimit / 1000000}MB`);
  }

  // Get current authenticated user as a fallback
  const {
    data: { user },
  } = await supabase.auth.getUser();
  const currentUserId = user?.id;

  // Ensure we have valid IDs - prioritize user_id since that's what's available
  const userId =
    workspace.user_id || workspace.owner_id || currentUserId || null;

  // If we don't have a valid userId, throw an error rather than proceeding with an invalid ID
  if (!userId) {
    throw new Error("No valid user ID found for workspace image upload");
  }

  // Determine the ID to use for workspace
  const workspaceId =
    workspace.id && typeof workspace.id === "string" ? workspace.id : null;

  // If we don't have a valid workspaceId, throw an error
  if (!workspaceId) {
    throw new Error("No valid workspace ID found for image upload");
  }

  const currentPath = workspace.image_path || "";
  // Use the correct path structure: userId/workspaces/workspaceId/timestamp
  let filePath = `${userId}/workspaces/${workspaceId}/${Date.now()}`;

  if (currentPath && currentPath.length > 0) {
    try {
      // Delete the old image
      const { error: deleteError } = await supabase.storage
        .from(bucket)
        .remove([currentPath]);

      if (deleteError) {
        // Error deleting old image
      }
    } catch (error) {
      // Error during old image deletion
    }
  }

  // Use the same upload approach as assistant images
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(filePath, image, {
      upsert: true,
    });

  if (error) {
    throw new Error("Error uploading image: " + error.message);
  }

  // Verify the URL works immediately after upload
  const isAccessible = await verifyImageAccessibility(filePath);

  // Get test URLs
  const publicUrl = getWorkspaceImageUrl(filePath);
  const signedUrl = await getWorkspaceImageFromStorage(filePath);

  return filePath;
};

/**
 * Helper function to convert an image to base64
 * Includes image type in the prefix for proper rendering
 */
const convertImageToBase64 = async (imageFile) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        // The result includes the data URL prefix which we want to keep for MIME type info
        const base64WithPrefix = reader.result;

        // Add our own prefix to identify it as a base64 encoded image
        resolve(`base64data:${base64WithPrefix}`);
      } catch (error) {
        reject(new Error("Error processing image data: " + error.message));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(imageFile);
  });
};

/**
 * Try to find workspace image in alternate member paths if the primary path fails
 * @param {string} originalPath - The original path that failed
 * @returns {Promise<string|null>} - The signed URL or null
 */
const findImageInMemberPaths = async (originalPath) => {
  if (!originalPath) return null;

  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    // Parse the original path to extract components
    const pathParts = originalPath.split("/");
    if (pathParts.length < 3) return null;

    // Extract the filename (the last part)
    const filename = pathParts[pathParts.length - 1];
    // Extract the workspaceId (usually the second to last part)
    const workspaceId = pathParts[pathParts.length - 2];

    // Check if we can find this workspace
    const { data: workspace, error: workspaceError } = await supabase
      .from("workspaces")
      .select("workspace_members, owner_id")
      .eq("id", workspaceId)
      .single();

    if (workspaceError) {
      return null;
    }

    // Combine owner and members
    const allMembers = [...(workspace.workspace_members || [])];
    if (workspace.owner_id && !allMembers.includes(workspace.owner_id)) {
      allMembers.push(workspace.owner_id);
    }

    // Try each member's path
    for (const memberId of allMembers) {
      // Skip the original path (which already failed)
      const memberPathStart = pathParts[0];
      if (memberId === memberPathStart) continue;

      // Create a member-specific path
      const memberPath = `${memberId}/workspaces/${workspaceId}/${filename}`;

      try {
        // Try to get a signed URL for this path
        const { data, error } = await supabase.storage
          .from("avatars")
          .createSignedUrl(memberPath, 60 * 60 * 24); // 1 day

        if (!error && data?.signedUrl) {
          return data.signedUrl;
        }
      } catch (memberError) {
        // Continue trying other members
      }
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Retrieves workspace image URL from storage path
 * @param {string} filePath - The path to the image in storage
 * @returns {Promise<string|null>} - The signed URL or null
 */
export const getWorkspaceImageFromStorage = async (filePath) => {
  if (!filePath) {
    return null;
  }

  // If it's a base64 data URL, return it directly
  if (typeof filePath === "string" && filePath.startsWith("base64data:")) {
    return filePath.substring(10); // Remove prefix
  }

  try {
    const { data, error } = await supabase.storage
      .from("avatars")
      .createSignedUrl(filePath, 60 * 60 * 24 * 30); // 30 days

    if (error) {
      // First, try to find the image in member paths
      const memberPathUrl = await findImageInMemberPaths(filePath);
      if (memberPathUrl) {
        return memberPathUrl;
      }

      // If member paths fail, try direct download for backward compatibility
      try {
        const { data: fileData } = await supabase.storage
          .from("avatars")
          .download(filePath);

        if (fileData) {
          const url = URL.createObjectURL(fileData);
          return url;
        }
      } catch (downloadError) {
        // Error trying direct download
      }

      // Fall back to public URL
      const publicUrl = getWorkspaceImageUrl(filePath);
      return publicUrl;
    }

    return data.signedUrl;
  } catch (error) {
    // Fall back to public URL
    const publicUrl = getWorkspaceImageUrl(filePath);
    return publicUrl;
  }
};

/**
 * Gets a public URL for a workspace image
 * @param {string} imagePath - The path to the image in storage
 * @returns {string|null} - The public URL or null
 */
export const getWorkspaceImageUrl = (imagePath) => {
  if (!imagePath) {
    return null;
  }

  // Check if this is a base64 encoded image from previous implementation
  if (typeof imagePath === "string" && imagePath.startsWith("base64data:")) {
    return imagePath.substring(10); // Remove base64data: prefix to get the data URL
  }

  // Check if it's already a full URL
  if (typeof imagePath === "string" && imagePath.startsWith("http")) {
    return imagePath;
  }

  // Ensure the path is a string to avoid errors
  if (typeof imagePath !== "string") {
    return null;
  }

  // Generate the public URL - make sure VITE_SUPABASE_URL is set correctly
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    return null;
  }

  // Handle paths that already have the avatars/ prefix to avoid double prefix
  let cleanPath = imagePath;
  if (typeof imagePath === "string" && imagePath.startsWith("avatars/")) {
    cleanPath = imagePath.substring(8); // Remove "avatars/" prefix
  }

  const url = `${supabaseUrl}/storage/v1/object/public/avatars/${cleanPath}`;
  return url;
};

/**
 * Gets the best available URL for a workspace image (for immediate display)
 * This is a synchronous function that returns the best immediate URL
 * @param {string} imagePath - The path to the image
 * @param {string} fallbackUrl - Optional fallback URL if no image is available
 * @returns {string} - The image URL or fallback
 */
export const getWorkspaceImageUrlSync = (imagePath, fallbackUrl = null) => {
  if (!imagePath) {
    return fallbackUrl;
  }

  // Check if this is a base64 image
  if (typeof imagePath === "string" && imagePath.startsWith("base64data:")) {
    return imagePath.substring(10); // Remove prefix
  }

  // Check if it's already a full URL
  if (typeof imagePath === "string" && imagePath.startsWith("http")) {
    return imagePath;
  }

  // Return the public URL - make sure VITE_SUPABASE_URL is set correctly
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    return fallbackUrl;
  }

  // Handle paths that already have the avatars/ prefix to avoid double prefix
  let cleanPath = imagePath;
  if (typeof imagePath === "string" && imagePath.startsWith("avatars/")) {
    cleanPath = imagePath.substring(8); // Remove "avatars/" prefix
  }

  const url = `${supabaseUrl}/storage/v1/object/public/avatars/${cleanPath}`;
  return url;
};

// After the upload completes, verify the image is accessible
export const verifyImageAccessibility = async (filePath) => {
  if (!filePath) {
    return false;
  }

  try {
    // Try to get a signed URL as first verification
    const { data, error } = await supabase.storage
      .from("avatars")
      .createSignedUrl(filePath, 60);

    if (error) {
      // Try direct download as a backup check
      try {
        const { data: downloadData, error: downloadError } =
          await supabase.storage.from("avatars").download(filePath);

        if (downloadError) {
          return false;
        }

        return true;
      } catch (downloadErr) {
        return false;
      }
    }

    return true;
  } catch (error) {
    return false;
  }
};
